import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import RaytechLogo from "@/components/brand/RaytechLogo";

const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const links = [
    { to: "/", label: t.nav.home, key: "home" },
    { to: "/services", label: t.nav.services, key: "services" },
    { to: "/about", label: t.nav.about, key: "about" },
    { to: "/contact", label: t.nav.contact, key: "contact" },
  ];

  return (
    <header
      className="sticky top-0 z-50 w-full backdrop-blur-xl bg-white/80 border-b border-brand-border"
      data-testid="site-header"
    >
      <div className="container-x flex items-center justify-between h-16 sm:h-20">
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="navbar-logo-link"
          onClick={() => setOpen(false)}
        >
          <RaytechLogo size={36} variant="dark" />
          <span className="font-heading font-bold tracking-tight text-brand-ink text-lg leading-none">
            RAYTECH
            <span className="block text-[10px] tracking-[0.32em] font-medium text-brand-deep mt-1">
              ADVISORY
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.key}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.key}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? "text-brand-deep underline-deep"
                    : "text-brand-ink/70 hover:text-brand-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="language-toggle"
            className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold tracking-[0.18em] uppercase text-brand-ink/70 hover:text-brand-deep border border-brand-border px-3 py-1.5 transition-colors"
            aria-label="Toggle language"
          >
            <span className={lang === "fr" ? "text-brand-deep" : ""}>FR</span>
            <span className="text-brand-border">/</span>
            <span className={lang === "en" ? "text-brand-deep" : ""}>EN</span>
          </button>

          <Link to="/contact" className="hidden md:inline-flex" data-testid="navbar-cta">
            <Button className="rounded-none bg-brand-ink hover:bg-brand-deep text-white font-medium h-10 px-5">
              {t.nav.cta}
            </Button>
          </Link>

          <button
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden p-2 text-brand-ink"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-brand-border bg-white"
          data-testid="mobile-menu"
        >
          <div className="container-x py-6 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.key}`}
                className={({ isActive }) =>
                  `py-3 text-base font-medium ${
                    isActive ? "text-brand-deep" : "text-brand-ink/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button
              onClick={toggle}
              data-testid="mobile-language-toggle"
              className="mt-3 self-start text-xs font-semibold tracking-[0.18em] uppercase text-brand-ink/70 border border-brand-border px-3 py-1.5"
            >
              {lang === "fr" ? "FR / en" : "fr / EN"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
